/**
 * Leveled logger with module-specific filtering (log4j-style).
 *
 * Log levels (from most to least verbose):
 * - DEBUG: Detailed debugging info
 * - INFO: General information
 * - WARN: Warning messages
 * - ERROR: Error messages
 * - NONE: No logging
 *
 * Environment variables:
 * - LOGGING_LEVEL: Global log level (default: INFO in dev, ERROR in prod)
 * - LOG_MODULES: Module-specific log levels (like log4j)
 *   Format: ModuleName:level,OtherModule:level
 *   Examples:
 *     LOG_MODULES=xdb:DEBUG,viewerContext:INFO     # explicit levels
 *     LOG_MODULES=xdb,viewerContext                # no level = DEBUG
 *     LOG_MODULES=xdb                              # single module, DEBUG
 *
 * Client-side uses NEXT_PUBLIC_ prefix:
 * - NEXT_PUBLIC_LOG_MODULES=AuthFlow:DEBUG
 *
 * Usage:
 *   import { createLogger } from '@/lib/utils/logger';
 *   const logger = createLogger('myModule');
 *   logger.debug('Starting export', { projectId });
 *   logger.info('User action completed');
 *   logger.warn('Unusual state detected');
 *   logger.error('Export failed', error);
 */

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'NONE';

const LOG_LEVELS: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4,
};

// ANSI color codes for terminal output
const COLORS = {
  reset: '\x1b[0m',
  dim: '\x1b[2m',
  cyan: '\x1b[36m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  red: '\x1b[31m',
  magenta: '\x1b[35m',
};

// Determine current log level
const isDev = typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';
const isLocal = typeof process !== 'undefined' && typeof process.env?.VERCEL_ENV === 'undefined';
const isServer = typeof window === 'undefined';

// Only use colors in server-side dev/local environments (not in production logs or browser)
const useColors = isServer && (isDev || isLocal);

// Parse LOG_MODULES env var for module-level filtering (like log4j)
// Format: ModuleName:level,OtherModule:level
// Example: LOG_MODULES=midjenClient:DEBUG,StudioProject:INFO
// Client-side uses NEXT_PUBLIC_LOG_MODULES
function parseLogModules(): Map<string, LogLevel> {
  const moduleLogLevels = new Map<string, LogLevel>();

  if (typeof process === 'undefined') {
    return moduleLogLevels;
  }

  // Check both server and client env vars
  const logModulesEnv = process.env.LOG_MODULES || process.env.NEXT_PUBLIC_LOG_MODULES;

  if (logModulesEnv) {
    for (const entry of logModulesEnv.split(',')) {
      const [module, level] = entry.split(':').map((s) => s.trim());
      if (module && level) {
        const upperLevel = level.toUpperCase() as LogLevel;
        if (LOG_LEVELS[upperLevel] !== undefined) {
          moduleLogLevels.set(module, upperLevel);
        }
      } else if (module) {
        // If no level specified, default to DEBUG
        moduleLogLevels.set(module, 'DEBUG');
      }
    }
  }

  return moduleLogLevels;
}

const moduleLogLevels = parseLogModules();

const getDefaultLogLevel = (): LogLevel => {
  // Only check env vars if we're in a Node environment (server-side)
  if (typeof process === 'undefined') {
    // Client-side: default to INFO in dev mode (based on NODE_ENV baked in at build time)
    return 'INFO';
  }

  // Explicit override via env var
  const envLevel = process.env.LOGGING_LEVEL?.toUpperCase() as LogLevel;
  if (envLevel) {
    if (LOG_LEVELS[envLevel] !== undefined) {
      return envLevel;
    }
    // Invalid level specified, warn and use default
    console.warn(
      `[LOGGER] Invalid LOGGING_LEVEL: "${process.env.LOGGING_LEVEL}". Valid values: DEBUG, INFO, WARN, ERROR, NONE`
    );
  }

  // Default: INFO in dev/local, ERROR in production
  return isDev || isLocal ? 'INFO' : 'ERROR';
};

const DEFAULT_LOG_LEVEL = getDefaultLogLevel();

function getModuleLogLevel(module?: string): number {
  if (module && moduleLogLevels.has(module)) {
    return LOG_LEVELS[moduleLogLevels.get(module)!];
  }
  return LOG_LEVELS[DEFAULT_LOG_LEVEL];
}

export interface Logger {
  log: (...args: any[]) => void; // for swapping with console.log
  debug: (...args: any[]) => void;
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
}

/**
 * Create a module-specific logger.
 *
 * When LOG_MODULES is set, each module gets its configured level.
 * Example: LOG_MODULES=midjenClient:DEBUG,StudioProject:INFO
 *
 * @example
 * const logger = createLogger('midjenClient');
 * logger.info({ userId }, 'User action completed');
 */
export function createLogger(module: string): Logger {
  const moduleLevel = getModuleLogLevel(module);

  // Format prefix with optional colors
  const formatPrefix = (level: string, color: string) => {
    if (useColors) {
      return `${color}[${level}]${COLORS.reset} ${COLORS.magenta}[${module}]${COLORS.reset}`;
    }
    return `[${level}] [${module}]`;
  };

  return {
    log: (...args: any[]) => {
      if (moduleLevel <= LOG_LEVELS.DEBUG) {
        console.log(formatPrefix('DEBUG', COLORS.dim), ...args);
      }
    },
    debug: (...args: any[]) => {
      if (moduleLevel <= LOG_LEVELS.DEBUG) {
        console.log(formatPrefix('DEBUG', COLORS.dim), ...args);
      }
    },

    info: (...args: any[]) => {
      if (moduleLevel <= LOG_LEVELS.INFO) {
        console.info(formatPrefix('INFO', COLORS.cyan), ...args);
      }
    },

    warn: (...args: any[]) => {
      if (moduleLevel <= LOG_LEVELS.WARN) {
        console.warn(formatPrefix('WARN', COLORS.yellow), ...args);
      }
    },

    error: (...args: any[]) => {
      if (moduleLevel <= LOG_LEVELS.ERROR) {
        console.error(formatPrefix('ERROR', COLORS.red), ...args);
      }
    },
  };
}

/**
 * @deprecated Use createLogger('moduleName') instead for module-specific logging.
 */
export const logger = createLogger('root');
